import { inject, Injectable } from '@angular/core';
import { IPageSizeStore, PAGE_SIZE_CONFIG, PAGE_SIZE_GLOBAL_DEFAULT, PageSizeEntry } from '@/core/util/page-size-store.interface';
import { environment } from '../../../environments/environment';

/**
 * Persists the selected page size to localStorage.
 *
 * Reads its storage key and default size from PAGE_SIZE_CONFIG using the
 * feature key supplied by BaseList at construction time. Falls back to
 * PAGE_SIZE_GLOBAL_DEFAULT when the feature is not listed in the config.
 *
 * No changes are needed in feature components — the store resolves itself
 * automatically once BaseList calls setFeatureKey(route.key).
 */
@Injectable({ providedIn: 'root' })
export class LocalStoragePageSizeStore implements IPageSizeStore {
    private readonly config = inject(PAGE_SIZE_CONFIG, { optional: true }) as PageSizeEntry | null;
    private readonly globalDefault = inject(PAGE_SIZE_GLOBAL_DEFAULT);

    private storageKey = `${environment.appKeyPrefix}:mpp`; // safe fallback until setFeatureKey() fires
    private defaultSize = this.globalDefault;

    setFeatureKey(featureKey: string): void {
        if (this.config?.separate) {
            this.storageKey = `${environment.appKeyPrefix}:mpp_${featureKey}`;
            this.defaultSize = this.globalDefault;
        }
    }

    get(): number {
        const stored = localStorage.getItem(this.storageKey);
        return stored !== null ? +stored : this.defaultSize;
    }

    set(size: number): void {
        localStorage.setItem(this.storageKey, size.toString());
    }
}
